import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Search, X, CheckCircle, XCircle, DollarSign, RefreshCw, Filter } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { emailService } from '../../lib/emailService';
import Pagination from '../../components/Pagination';
import { formatCurrency } from '../../lib/currencyUtils';

interface Booking {
  id: string;
  user_id: string;
  track_type: 'drag' | 'drift';
  booking_type: 'individual' | 'team' | 'exclusive';
  time_slot: 'morning' | 'afternoon' | 'evening' | 'night' | null;
  booking_date: string;
  duration_hours: number;
  use_traction_compound: boolean;
  total_amount: number;
  status: string;
  payment_status: string;
  special_requests?: string;
  deposit_amount: number;
  deposit_paid: boolean;
  remaining_balance: number;
  start_time?: string;
  end_time?: string;
  created_at: string;
  users: {
    full_name: string;
    email: string;
    phone: string;
  };
}

export default function BookingManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string>('all');
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchBookings();
  }, [user]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, selectedPaymentStatus, showUpcomingOnly]);

  // Helper function to format date for display (avoiding timezone issues)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Check if a booking date is in the past
  const isBookingInPast = (bookingDate: string): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    const [year, month, day] = bookingDate.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date < today;
  };

  const fetchBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('track_bookings')
        .select(`
          *,
          users(full_name, email, phone)
        `)
        .order('booking_date', { ascending: false });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Mark as fully paid (total + deposit) and confirm booking
  const markAsFullyPaid = async (bookingId: string) => {
    try {
      const { error } = await supabase
        .from('track_bookings')
        .update({ 
          payment_status: 'paid',
          deposit_paid: true,
          status: 'confirmed'
        })
        .eq('id', bookingId);

      if (error) throw error;

      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        await sendNotification(
          booking.user_id,
          `Your ${booking.track_type} track booking for ${formatDateForDisplay(booking.booking_date)} is now fully paid and confirmed!`,
          `Booking Confirmed: Fully Paid`
        );
      }

      toast.success('Booking marked as fully paid and confirmed!');
      fetchBookings();
    } catch (error) {
      console.error('Error marking as fully paid:', error);
      toast.error('Failed to mark as fully paid');
    }
  };

  // Mark deposit as paid only
  const markDepositPaid = async (bookingId: string) => {
    try {
      const { error } = await supabase
        .from('track_bookings')
        .update({ 
          deposit_paid: true,
          payment_status: 'deposit_paid'
        })
        .eq('id', bookingId);

      if (error) throw error;

      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        await sendNotification(
          booking.user_id,
          `Security deposit received for your ${booking.track_type} track booking on ${formatDateForDisplay(booking.booking_date)}`,
          `Deposit Received`
        );
      }

      toast.success('Deposit marked as paid!');
      fetchBookings();
    } catch (error) {
      console.error('Error marking deposit as paid:', error);
      toast.error('Failed to mark deposit as paid');
    }
  };

  // Process refund (refund security deposit)
  const processRefund = async (bookingId: string) => {
    if (!confirm('Are you sure you want to process a refund? This will refund the security deposit and mark the booking as completed.')) return;

    try {
      const { error } = await supabase
        .from('track_bookings')
        .update({ 
          payment_status: 'refunded',
          status: 'completed'
        })
        .eq('id', bookingId);

      if (error) throw error;

      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        await sendNotification(
          booking.user_id,
          `Security deposit of ${formatCurrency(booking.deposit_amount)} has been refunded for your ${booking.track_type} track booking on ${formatDateForDisplay(booking.booking_date)}`,
          `Security Deposit Refunded`
        );
      }

      toast.success('Security deposit refunded successfully!');
      fetchBookings();
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error('Failed to process refund');
    }
  };

  // Cancel booking and make date available
  const cancelBooking = async (bookingId: string) => {
    if (!confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('track_bookings')
        .update({ 
          status: 'cancelled',
          payment_status: 'cancelled'
        })
        .eq('id', bookingId);

      if (error) throw error;

      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        await sendNotification(
          booking.user_id,
          `Your ${booking.track_type} track booking for ${formatDateForDisplay(booking.booking_date)} has been cancelled`,
          `Booking Cancelled`
        );
      }

      toast.success('Booking cancelled successfully! Date is now available for other bookings.');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking');
    }
  };

  const sendNotification = async (userId: string, message: string, title: string) => {
    try {
      // Note: This assumes you have a notifications table, if not, this will silently fail
      const { error } = await supabase
        .from('notifications')
        .insert([
          {
            user_id: userId,
            message,
            title,
            type: 'booking_update'
          }
        ]);

      if (error) {
        console.log('Notification table not found, skipping notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  };

  // Calculate total amount to pay (track fee + deposit)
  const getTotalAmountToPay = (booking: Booking) => {
    return booking.total_amount + booking.deposit_amount;
  };

  // Calculate amount after refund (track fee only, deposit refunded)
  const getAmountAfterRefund = (booking: Booking) => {
    return booking.total_amount;
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.users.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.users.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.track_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.booking_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || booking.status === selectedStatus;
    const matchesPaymentStatus = selectedPaymentStatus === 'all' || booking.payment_status === selectedPaymentStatus;
    const matchesDateFilter = showUpcomingOnly ? !isBookingInPast(booking.booking_date) : true;
    
    return matchesSearch && matchesStatus && matchesPaymentStatus && matchesDateFilter;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = filteredBookings.slice(startIndex, endIndex);

  const formatTimeSlot = (timeSlot: string | null) => {
    if (!timeSlot) return '';
    return timeSlot.charAt(0).toUpperCase() + timeSlot.slice(1);
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getTimeDisplay = (booking: Booking) => {
    if (booking.start_time && booking.end_time) {
      return `${formatTime(booking.start_time)} - ${formatTime(booking.end_time)}`;
    }
    return formatTimeSlot(booking.time_slot);
  };

  const formatTrackType = (trackType: string) => {
    return trackType.charAt(0).toUpperCase() + trackType.slice(1);
  };

  const formatBookingType = (bookingType: string) => {
    return bookingType.charAt(0).toUpperCase() + bookingType.slice(1);
  };

  // Calculate total revenue from completed bookings and deposits
  const calculateTotalRevenue = () => {
    return bookings.reduce((sum, booking) => {
      // Only count revenue from paid bookings
      if (booking.status === 'cancelled') {
        return sum; // Don't count cancelled bookings
      }
      
      if (booking.payment_status === 'paid') {
        return sum + booking.total_amount; // Full payment
      } else if (booking.payment_status === 'deposit_paid' || booking.deposit_paid) {
        return sum + booking.deposit_amount; // Only deposit paid
      } else if (booking.payment_status === 'refunded') {
        return sum + booking.total_amount; // Only track fee (deposit refunded)
      }
      
      return sum; // No payment
    }, 0);
  };

  // Resend booking confirmation email
  const resendConfirmationEmail = async (bookingId: string) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) {
        toast.error('Booking not found');
        return;
      }

      // Prepare email data
      const emailData = {
        customer_name: booking.users.full_name,
        track_type: formatTrackType(booking.track_type),
        booking_date: formatDateForDisplay(booking.booking_date),
        booking_time: getTimeDisplay(booking),
        duration: booking.duration_hours,
        booking_type: formatBookingType(booking.booking_type),
        traction_compound: booking.use_traction_compound,
        special_requests: booking.special_requests,
        total_amount: booking.total_amount,
        deposit_amount: booking.deposit_amount
      };

      // Send email
      await emailService.sendTrackBookingConfirmation(
        booking.user_id,
        booking.users.email,
        emailData
      );

      toast.success('Confirmation email resent successfully!');
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      toast.error('Failed to resend confirmation email');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-black py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-white mb-8">Track Booking Management</h1>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <Calendar className="h-8 w-8 text-primary-500" />
              <div>
                <h3 className="text-lg font-semibold text-white">Total Bookings</h3>
                <p className="text-2xl font-bold text-primary-500">{bookings.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <h3 className="text-lg font-semibold text-white">Confirmed</h3>
                <p className="text-2xl font-bold text-green-500">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <XCircle className="h-8 w-8 text-yellow-500" />
              <div>
                <h3 className="text-lg font-semibold text-white">Pending</h3>
                <p className="text-2xl font-bold text-yellow-500">
                  {bookings.filter(b => b.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900 p-6 rounded-lg">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8 text-primary-500" />
              <div>
                <h3 className="text-lg font-semibold text-white">Total Revenue</h3>
                <p className="text-2xl font-bold text-primary-500">
                  {formatCurrency(calculateTotalRevenue())}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-900 text-white pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
            />
          </div>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={selectedPaymentStatus}
            onChange={(e) => setSelectedPaymentStatus(e.target.value)}
            className="bg-zinc-900 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:outline-none"
          >
            <option value="all">All Payment Status</option>
            <option value="pending">Pending</option>
            <option value="deposit_paid">Deposit Paid</option>
            <option value="paid">Fully Paid</option>
            <option value="refunded">Refunded</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <button 
            onClick={() => setShowUpcomingOnly(!showUpcomingOnly)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showUpcomingOnly
                ? 'bg-primary-600 text-white'
                : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            {showUpcomingOnly ? 'Upcoming Only' : 'All Bookings'}
          </button>
        </div>

        {/* Bookings Table */}
        <div className="bg-zinc-900 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zinc-800">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Track Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Payment Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {currentBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-zinc-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white">
                          {booking.users.full_name}
                        </div>
                        <div className="text-sm text-gray-400">
                          {booking.users.email}
                        </div>
                        <div className="text-sm text-gray-400">
                          {booking.users.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">
                        {formatTrackType(booking.track_type)} Track
                      </div>
                      <div className="text-sm text-gray-400">
                        {formatBookingType(booking.booking_type)} • {booking.duration_hours}h
                      </div>
                      {booking.use_traction_compound && (
                        <div className="text-sm text-primary-400">
                          + Traction Compound
                        </div>
                      )}
                      {booking.special_requests && (
                        <div className="text-sm text-gray-400">
                          Note: {booking.special_requests}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">
                        {formatDateForDisplay(booking.booking_date)}
                        {isBookingInPast(booking.booking_date) && (
                          <span className="ml-2 text-xs bg-gray-700 text-gray-300 px-1 py-0.5 rounded">Past</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {getTimeDisplay(booking)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-white">
                        Track Fee: {formatCurrency(booking.total_amount)}
                      </div>
                      {booking.deposit_amount > 0 && (
                        <div className="text-sm text-gray-400">
                          Security Deposit: {formatCurrency(booking.deposit_amount)}
                          <span className={booking.deposit_paid ? 'text-green-400' : 'text-primary-400'}>
                            {booking.deposit_paid ? ' ✓' : ' ✗'}
                          </span>
                        </div>
                      )}
                      <div className="text-sm font-semibold text-primary-400">
                        {booking.payment_status === 'refunded' ? (
                          <>Total: {formatCurrency(getAmountAfterRefund(booking))} <span className="text-xs">(deposit refunded)</span></>
                        ) : (
                          <>Total: {formatCurrency(getTotalAmountToPay(booking))}</>
                        )}
                      </div>
                      <div className={`text-xs font-semibold ${
                        booking.payment_status === 'paid' ? 'text-green-400' : 
                        booking.payment_status === 'deposit_paid' ? 'text-blue-400' :
                        booking.payment_status === 'refunded' ? 'text-purple-400' :
                        booking.payment_status === 'pending' ? 'text-yellow-400' : 
                        'text-primary-400'
                      }`}>
                        {booking.payment_status.toUpperCase().replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 
                          booking.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                          booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {booking.status !== 'completed' && booking.status !== 'cancelled' ? (
                        <div className="flex flex-col gap-2">
                          {/* Primary Action: Mark as Fully Paid (Track Fee + Deposit) */}
                          {booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
                            <button
                              onClick={() => markAsFullyPaid(booking.id)}
                              className="text-white hover:text-green-100 text-sm px-3 py-2 bg-green-600 hover:bg-green-700 rounded font-semibold transition-colors"
                              title="Mark as Fully Paid & Confirmed"
                            >
                              Mark Fully Paid
                            </button>
                          )}

                          {/* Secondary Actions */}
                          <div className="flex gap-1">
                            {/* Deposit Only */}
                            {!booking.deposit_paid && booking.deposit_amount > 0 && booking.status !== 'cancelled' && (
                              <button
                                onClick={() => markDepositPaid(booking.id)}
                                className="text-white hover:text-blue-100 text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                                title="Mark Deposit as Paid"
                              >
                                Deposit Paid
                              </button>
                            )}

                            {/* Refund (only available after fully paid) */}
                            {booking.payment_status === 'paid' && booking.deposit_amount > 0 && (
                              <button
                                onClick={() => processRefund(booking.id)}
                                className="text-white hover:text-blue-100 text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                                title="Refund Security Deposit"
                              >
                                Refund
                              </button>
                            )}

                            {/* Resend Email */}
                            <button
                              onClick={() => resendConfirmationEmail(booking.id)}
                              className="text-white hover:text-purple-100 text-xs px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded transition-colors"
                              title="Resend Confirmation Email"
                            >
                              <RefreshCw className="h-3 w-3" />
                            </button>

                            {/* Cancel */}
                            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                              <button
                                onClick={() => cancelBooking(booking.id)}
                                className="text-white hover:text-primary-100 text-xs px-2 py-1 bg-primary-600 hover:bg-primary-700 rounded transition-colors"
                                title="Cancel Booking"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          {booking.status === 'completed' ? 'Completed' : 'Cancelled'}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={filteredBookings.length}
          />
        </div>

        {filteredBookings.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">No bookings found</div>
          </div>
        )}
      </div>
    </div>
  );
}